"use client";

import React from "react";
import { Clock, Lightbulb, Scale, MapPin, FileText, Hourglass, TrendingUp, Award, Users, Gavel } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const factors = [{
  title: "Time and labor required",
  icon: Clock,
  description: "Evaluating the actual hours expended and the necessity of that time given the specific demands of the matter."
}, {
  title: "Novelty and difficulty",
  icon: Lightbulb,
  description: "Assessing the complexity of the legal issues and any unique challenges presented during representation."
}, {
  title: "Skill required",
  icon: Scale,
  description: "Considering the level of specialized legal expertise necessary to perform the required services properly."
}, {
  title: "Customary local rates",
  icon: MapPin,
  description: "Comparing the requested rates to the fees customarily charged in the locality for similar legal services."
}, {
  title: "Fixed or contingent fee",
  icon: FileText,
  description: "Analyzing how the initial fee arrangement impacts the reasonableness of the final claim."
}, {
  title: "Time limitations",
  icon: Hourglass,
  description: "Factoring in any urgent constraints or strict deadlines imposed by the client or the circumstances."
}, {
  title: "Amount and results",
  icon: TrendingUp,
  description: "Weighing the financial stakes of the matter against the ultimate outcome achieved for the client."
}, {
  title: "Experience and reputation",
  icon: Award,
  description: "Recognizing the professional standing, years of practice, and demonstrated ability of the counsel involved."
}, {
  title: "Professional relationship",
  icon: Users,
  description: "Considering how the history and duration of the professional relationship with the client contextualizes the work."
}, {
  title: "Awards in Similar Matters",
  icon: Gavel,
  description: "Reviewing comparisons to fee awards made by courts in similar legal matters within the jurisdiction."
}, {
  title: "Preclusion of Other Employment",
  icon: Users,
  description: "Whether acceptance of the matter affected the attorney's ability to accept other employment."
}, {
  title: "Undesirability of the Case",
  icon: Gavel,
  description: "Whether the nature or circumstances of the matter affected its desirability."
}];
export default function AttorneyFeeFactors() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="attorneyfeefactors" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-8 md:mb-12 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground tracking-tight mb-6">
            Factors Considered in Attorney's Fee Disputes
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            Evaluation of attorney's fees considers the nature of work performed and these factors to determine reasonableness
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {factors.map((factor, i) => <FactorCard key={i} factor={factor} index={i} />)}
        </div>
      </div>
    </section>;
}
function FactorCard({
  factor,
  index
}: {
  factor: typeof factors[0];
  index: number;
}) {
  const reveal = useRevealOnScroll({
    threshold: 0.1
  });
  const Icon = factor.icon;
  return <div data-index={index} ref={reveal.ref} className={`transition-all duration-700 ease-out h-full ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 75}ms`
  }}>
      <Card className="h-full bg-card border border-border/60 rounded-lg p-4 flex flex-col">
        <CardHeader className="pb-4 flex-none px-0 pt-0">
          <div className="w-10 h-10 rounded-md border border-border/80 bg-accent/30 flex items-center justify-center mb-2">
            <Icon className="w-5 h-5 text-secondary stroke-[1.5]" />
          </div>
          <CardTitle className="font-default text-lg font-medium text-foreground leading-tight">
            {factor.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-grow px-0 pb-0">
          <p className="text-xs text-muted-foreground font-sans leading-relaxed">
            {factor.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}